import React, { useRef, useState, useEffect, ReactNode } from 'react';
import './BaseScanner.css';
import { Camera, Upload, X, RefreshCw, AlertCircle, Lock } from 'lucide-react';

export interface BaseScannerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  defaultMode?: 'upload' | 'camera';
  frameType?: 'square' | 'card'; // square for QR, card for business card
  onCapture?: (imageData: string) => void; // For camera capture
  onFileUpload?: (imageData: string) => void; // For file upload
  renderContent?: (props: ScannerContentProps) => ReactNode; // Custom content renderer
  maxFileSize?: number; // in MB
  acceptedFormats?: string;
}

export interface ScannerContentProps {
  mode: 'upload' | 'camera';
  isStreaming: boolean;
  capturedImage: string | null;
  uploadedImage: string | null;
  error: string | null;
  isProcessing?: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameras: MediaDeviceInfo[];
  onCapture: () => void;
  onFileSelect: () => void;
  onRetake: () => void;
  onSwitchCamera: () => void;
  renderActions?: (image: string) => ReactNode;
}

export const BaseScanner: React.FC<BaseScannerProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  defaultMode = 'upload',
  frameType = 'square',
  onCapture,
  onFileUpload,
  renderContent,
  maxFileSize = 5,
  acceptedFormats = 'image/*',
}) => {
  const [mode, setMode] = useState<'upload' | 'camera'>(defaultMode);
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && mode === 'camera') {
      initializeCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, mode]);

  const initializeCamera = async () => {
    try {
      setError(null);
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length > 0) {
        setCameras(videoDevices);
        
        const backCameraIndex = videoDevices.findIndex(device =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );
        
        const selectedIndex = backCameraIndex >= 0 ? backCameraIndex : 0;
        setCurrentCameraIndex(selectedIndex);
        await startCamera(videoDevices[selectedIndex].deviceId);
      } else {
        setError('No cameras found on this device');
      }
    } catch (err) {
      console.error('[BaseScanner] ❌ Error initializing camera:', err);
      setError('Failed to initialize camera');
    }
  };

  const startCamera = async (deviceId?: string) => {
    try {
      setError(null);
      const constraints: MediaStreamConstraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : {
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Wait for video element to be available (retry mechanism)
      const attachStreamToVideo = async (retries = 0): Promise<void> => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;

          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current) {
              videoRef.current.play()
                .then(() => {
                  setIsStreaming(true);
                })
                .catch((err) => {
                  console.error('[BaseScanner] ❌ Error playing video:', err);
                  setError('Failed to start video playback.');
                });
            }
          };
        } else if (retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          return attachStreamToVideo(retries + 1);
        } else {
          console.error('[BaseScanner] ❌ videoRef.current is still null after 10 retries!');
          setError('Failed to initialize video element. Please try again.');
          // Stop the stream since we can't use it
          stream.getTracks().forEach(track => track.stop());
        }
      };

      await attachStreamToVideo();
    } catch (err: any) {
      console.error('[BaseScanner] ❌ Error accessing camera:', err);
      
      let errorMessage = '';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please allow camera access in your browser settings, or use the upload option.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found on this device. Please use the upload option instead.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is being used by another app. Please close other apps using the camera and try again.';
      } else {
        errorMessage = 'Could not start camera. Please try the upload option instead.';
      }
      
      setError(errorMessage);
      setIsStreaming(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) {
      console.error('[BaseScanner] ❌ videoRef.current is null, cannot capture');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageData);
      stopCamera();
      
      if (onCapture) {
        onCapture(imageData);
      }
    } else {
      console.error('[BaseScanner] ❌ Failed to get 2D context from canvas');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      // Reset file input so user can try again
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    if (file.size > maxFileSize * 1024 * 1024) {
      setError(`Image size must be less than ${maxFileSize}MB`);
      // Reset file input so user can try again
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    setIsLoadingFile(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result as string;
      setUploadedImage(imageData);
      setIsLoadingFile(false);
      
      // Reset file input after reading so next upload can select the same file if needed
      if (event.target) {
        event.target.value = '';
      }
      
      if (onFileUpload) {
        onFileUpload(imageData);
      }
    };
    reader.onerror = () => {
      setError('Failed to read image file');
      setIsLoadingFile(false);
      // Reset file input on error
      if (event.target) {
        event.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    // Clear previous uploaded image before opening file picker
    setUploadedImage(null);
    setError(null);
    fileInputRef.current?.click();
  };

  const handleModeChange = (newMode: 'upload' | 'camera') => {
    setError(null);
    setCapturedImage(null);
    setUploadedImage(null);
    // Reset file input when switching modes
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (mode === 'camera' && newMode !== 'camera') {
      stopCamera();
    }
    
    setMode(newMode);
    
    if (newMode === 'camera') {
      initializeCamera();
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setUploadedImage(null);
    setIsLoadingFile(false);
    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (mode === 'camera') {
      initializeCamera();
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) {
      return;
    }

    stopCamera();
    // Brief delay to ensure previous stream is fully released
    await new Promise(resolve => setTimeout(resolve, 300));

    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(nextIndex);
    await startCamera(cameras[nextIndex].deviceId);
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setUploadedImage(null);
    setError(null);
    setIsLoadingFile(false);
    // Reset file input on close
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setMode(defaultMode);
    onClose();
  };

  if (!isOpen) return null;

  const scannerContentProps: ScannerContentProps = {
    mode,
    isStreaming,
    capturedImage,
    uploadedImage,
    error,
    isProcessing: isLoadingFile,
    videoRef,
    fileInputRef,
    cameras,
    onCapture: capturePhoto,
    onFileSelect: triggerFileInput,
    onRetake: handleRetake,
    onSwitchCamera: switchCamera,
  };

  return (
    <div className="base-scanner-overlay" onClick={handleClose}>
      <div className="base-scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="base-scanner-header">
          <div className="scanner-title">
            {icon || <Camera size={24} />}
            <h2>{title}</h2>
          </div>
          <button onClick={handleClose} className="btn-close-scanner">
            <X size={24} />
          </button>
        </div>

        <div className="scanner-mode-toggle">
          <button
            className={`mode-button ${mode === 'upload' ? 'active' : ''}`}
            onClick={() => handleModeChange('upload')}
          >
            <Upload size={18} />
            <span>Upload</span>
          </button>
          <button
            className={`mode-button ${mode === 'camera' ? 'active' : ''}`}
            onClick={() => handleModeChange('camera')}
          >
            <Camera size={18} />
            <span>Camera</span>
          </button>
        </div>

        <div className="base-scanner-content">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {(() => {
            const customContent = renderContent?.(scannerContentProps);
            return customContent !== null && customContent !== undefined ? (
              customContent
            ) : (
              <DefaultScannerContent {...scannerContentProps} frameType={frameType} />
            );
          })()}
        </div>

        <div className="base-scanner-footer">
          <p className="scanner-privacy-note">
            <Lock size={14} />
            <span>{mode === 'upload' ? 'Image Processing is Secure' : 'Camera Feed is Processed in Real-time'}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// Default content renderer
const DefaultScannerContent: React.FC<ScannerContentProps & { frameType: 'square' | 'card' }> = ({
  mode,
  isStreaming,
  capturedImage,
  uploadedImage,
  error,
  isProcessing,
  videoRef,
  cameras,
  onCapture,
  onFileSelect,
  onRetake,
  onSwitchCamera,
  renderActions,
  frameType,
}) => {
  if (error) {
    return (
      <div className="scanner-error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button onClick={mode === 'upload' ? onFileSelect : onCapture} className="btn-retry">
          <RefreshCw size={20} />
          Try Again
        </button>
      </div>
    );
  }

  if (capturedImage || uploadedImage) {
    return (
      <div className="captured-image-preview">
        <img src={capturedImage || uploadedImage || ''} alt="Captured" />
        {renderActions ? (
          renderActions(capturedImage || uploadedImage || '')
        ) : (
          <div className="preview-actions">
            <button onClick={onRetake} className="btn-secondary">
              <RefreshCw size={20} />
              {mode === 'upload' ? 'Upload New' : 'Retake'}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (mode === 'upload') {
    return (
      <div className="upload-area">
        {isProcessing ? (
          <div className="upload-loading">
            <RefreshCw size={48} className="spinner" />
            <p>Loading image...</p>
          </div>
        ) : (
          <>
            <div className="upload-placeholder" onClick={onFileSelect}>
              <Upload size={64} />
              <h3>Upload Image</h3>
              <p>Click to select an image</p>
              <p className="upload-format">Supports: PNG, JPEG, WebP (Max 5MB)</p>
            </div>
            <button onClick={onFileSelect} className="btn-upload-primary">
              <Upload size={20} />
              Select Image
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="camera-view">
      <video 
        ref={videoRef} 
        autoPlay 
        playsInline 
        muted
        className="camera-video"
        style={{ display: isStreaming ? 'block' : 'none' }}
      />
      {isStreaming ? (
        <>
          <div className="camera-overlay">
            <div className={`frame ${frameType === 'card' ? 'card-frame' : 'qr-frame'}`}>
              <div className="frame-corner top-left"></div>
              <div className="frame-corner top-right"></div>
              <div className="frame-corner bottom-left"></div>
              <div className="frame-corner bottom-right"></div>
            </div>
          </div>
          <div className="camera-instructions">
            <p>Position the {frameType === 'card' ? 'business card' : 'QR code'} within the frame</p>
          </div>
          <button onClick={onCapture} className="btn-capture">
            <Camera size={24} />
            <span>Capture</span>
          </button>
          {cameras.length > 1 && (
            <button onClick={onSwitchCamera} className="btn-switch-camera">
              <RefreshCw size={20} />
              Switch Camera
            </button>
          )}
        </>
      ) : (
        <div className="camera-loading">
          <Camera size={64} />
          <p>Starting camera...</p>
        </div>
      )}
    </div>
  );
};

export default BaseScanner;
